import {
  LEGACY_DEFAULT_REGULAR_COLOR,
  createDefaultGradeProgramColors,
} from '../constants/programColorPalettes';
import { supabase } from '../supabaseClient';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import type { TrackProgramMode } from '../utils/trackProgramModeStorage';
import {
  emptyTrackHoursAdjustSettings,
  loadTrackHoursAdjustSettingsMap,
  saveTrackHoursAdjustSettingsMap,
  type TrackHoursAdjustTrackSettings,
  type TrackHoursTermScope,
} from '../utils/trackHoursTermScopeStorage';
import type { CalendarDay } from '../types/domain';
import {
  allowSchoolScopedCacheSeedAfterInvalidate,
  canWriteSchoolScopedDataCache,
  invalidateSchoolScopedDataCaches,
} from '../utils/schoolDataCacheVersion';
import { reseedGradeTrackProgramPrefsLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import { deviceCacheStorage, deviceCacheSetItem } from '../utils/deviceCacheStorage';

export interface PageSectionLayout {
  id: string;
  gradeIds: string[];
  /** 편제 페이지: 클래스 미연결 섹션의 데이터 원본으로 쓰는 클래스 id */
  sourceGradeId?: string;
  /** 편제 페이지: 클래스 미연결 상태에서도 저장·복원할 섹션 컬럼 */
  trackColumns?: PageSectionTrackColumn[];
  /** 학사일정 페이지: 섹션별 독립 학년도·방학 설정 */
  academicCalendarSettings?: PageSectionAcademicCalendarSettings;
  /** 학사일정 페이지: 섹션별 독립 공휴일·토일 수업일 */
  academicCalendarDays?: CalendarDay[];
}

export interface PageSectionTrackColumn {
  id?: string;
  name: string;
  aliases: string[];
  criteriaHours: number;
  trackCategory?: 'regular' | 'other';
  /** 시간표 텍스트 끝의 `(X)`를 편제로 카운팅할 때 쓰는 1글자 와일드카드 */
  wildcardChar?: string;
}

export interface PageSectionAcademicCalendarSettings {
  academicYear: number;
  yearStartsOn: string;
  yearEndsOn: string;
  summerBreakStartsOn: string;
  summerBreakEndsOn: string;
  winterBreakStartsOn: string;
  winterBreakEndsOn: string;
}

const DEFAULT_LAYOUT: PageSectionLayout[] = [{ id: 'base', gradeIds: [] }];
const SECTION_LAYOUT_CACHE_VERSION = 'v1';
/** `normalizeClassScheduleRows` 등 로직 변경 시 캐시만 분리(다른 키는 v1 유지) */
const CLASS_SCHEDULE_SETTINGS_CACHE_VERSION = 'v3';

function classScheduleCacheKey(workspaceId: string): string {
  return `cp.classScheduleSettings.${CLASS_SCHEDULE_SETTINGS_CACHE_VERSION}.${workspaceId}`;
}

/** 대시보드·프로그램 LS 복원: 네트워크 없이 기초 시간표(수업 스케줄)만 읽는다. */
export function loadClassScheduleSettingsCache(workspaceId: string): ClassScheduleSettings | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(classScheduleCacheKey(workspaceId));
    if (!raw) return null;
    return normalizeClassScheduleSettings(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** program_color_settings JSON 구조 변경 시 LS 무효화 */
const PROGRAM_COLOR_SETTINGS_CACHE_VERSION = 'v2';

function programColorCacheKey(workspaceId: string): string {
  return `cp.programColorSettings.${PROGRAM_COLOR_SETTINGS_CACHE_VERSION}.${workspaceId}`;
}

function normalizeLayouts(raw: unknown): PageSectionLayout[] {
  if (!Array.isArray(raw)) return DEFAULT_LAYOUT;
  const mapped = raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as {
        id?: unknown;
        gradeIds?: unknown;
        sourceGradeId?: unknown;
        trackColumns?: unknown;
        academicCalendarSettings?: unknown;
        academicCalendarDays?: unknown;
      };
      if (typeof record.id !== 'string') return null;
      const gradeIds = Array.isArray(record.gradeIds)
        ? record.gradeIds.filter((v): v is string => typeof v === 'string')
        : [];
      const sourceGradeId = typeof record.sourceGradeId === 'string' ? record.sourceGradeId : undefined;
      const trackColumns = Array.isArray(record.trackColumns)
        ? record.trackColumns
            .map((column) => {
              if (!column || typeof column !== 'object') return null;
              const c = column as Record<string, unknown>;
              const name = typeof c.name === 'string' ? c.name.trim() : '';
              if (!name) return null;
              const aliases = Array.isArray(c.aliases)
                ? c.aliases.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
                : [];
              const criteriaHours =
                typeof c.criteriaHours === 'number' && Number.isFinite(c.criteriaHours)
                  ? Math.min(999, Math.max(0, Math.floor(c.criteriaHours)))
                  : 0;
              const wildcardRaw = typeof c.wildcardChar === 'string' ? c.wildcardChar.trim() : '';
              const wildcardChar = wildcardRaw.match(/[가-힣]/)?.[0] ?? '';
              const normalized: PageSectionTrackColumn = {
                id: typeof c.id === 'string' ? c.id : undefined,
                name,
                aliases: aliases.length > 0 ? aliases : [name],
                criteriaHours,
                trackCategory: c.trackCategory === 'other' ? 'other' : 'regular',
                wildcardChar: wildcardChar || undefined,
              };
              return normalized;
            })
            .filter((v): v is PageSectionTrackColumn => v !== null)
        : undefined;
      const settingsRecord =
        record.academicCalendarSettings && typeof record.academicCalendarSettings === 'object'
          ? (record.academicCalendarSettings as Record<string, unknown>)
          : null;
      const academicYear = Number(settingsRecord?.academicYear);
      const academicCalendarSettings: PageSectionAcademicCalendarSettings | undefined =
        settingsRecord && Number.isFinite(academicYear) && academicYear >= 2000 && academicYear <= 2100
          ? {
              academicYear: Math.floor(academicYear),
              yearStartsOn: typeof settingsRecord.yearStartsOn === 'string' ? settingsRecord.yearStartsOn : '',
              yearEndsOn: typeof settingsRecord.yearEndsOn === 'string' ? settingsRecord.yearEndsOn : '',
              summerBreakStartsOn:
                typeof settingsRecord.summerBreakStartsOn === 'string' ? settingsRecord.summerBreakStartsOn : '',
              summerBreakEndsOn:
                typeof settingsRecord.summerBreakEndsOn === 'string' ? settingsRecord.summerBreakEndsOn : '',
              winterBreakStartsOn:
                typeof settingsRecord.winterBreakStartsOn === 'string' ? settingsRecord.winterBreakStartsOn : '',
              winterBreakEndsOn:
                typeof settingsRecord.winterBreakEndsOn === 'string' ? settingsRecord.winterBreakEndsOn : '',
            }
          : undefined;
      const academicCalendarDays = Array.isArray(record.academicCalendarDays)
        ? (record.academicCalendarDays.filter((d) => {
            if (!d || typeof d !== 'object') return false;
            const row = d as Record<string, unknown>;
            return (
              typeof row.day === 'string' &&
              (row.day_kind === 'holiday' || row.day_kind === 'closure' || row.day_kind === 'class')
            );
          }) as CalendarDay[])
        : undefined;
      const normalized: PageSectionLayout = {
        id: record.id,
        gradeIds,
        sourceGradeId,
        trackColumns,
        academicCalendarSettings,
        academicCalendarDays,
      };
      return normalized;
    })
    .filter((v): v is PageSectionLayout => v !== null);
  if (mapped.length === 0) return DEFAULT_LAYOUT;
  /** 저장된 섹션 목록을 그대로 유지 — 삭제된 base를 빈 섹션으로 되살리지 않음(최소 1개는 상위에서 보장) */
  return mapped;
}

function gradeTrackSectionsCacheKey(workspaceId: string): string {
  return `cp.gradeTrackSections.${SECTION_LAYOUT_CACHE_VERSION}.${workspaceId}`;
}

function academicCalendarSectionsCacheKey(workspaceId: string): string {
  return `cp.academicCalendarSections.${SECTION_LAYOUT_CACHE_VERSION}.${workspaceId}`;
}

export function loadGradeTrackSectionsCache(workspaceId: string): PageSectionLayout[] | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(gradeTrackSectionsCacheKey(workspaceId));
    if (raw == null || raw === '') return null;
    return normalizeLayouts(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function saveGradeTrackSectionsCache(workspaceId: string, sections: PageSectionLayout[]): void {
  if (!workspaceId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  const normalized = normalizeLayouts(sections);
  deviceCacheSetItem(
    gradeTrackSectionsCacheKey(workspaceId),
    JSON.stringify(normalized)
  );
}

export function loadAcademicCalendarSectionsCache(workspaceId: string): PageSectionLayout[] | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(academicCalendarSectionsCacheKey(workspaceId));
    if (raw == null || raw === '') return null;
    return normalizeLayouts(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function saveAcademicCalendarSectionsCache(workspaceId: string, sections: PageSectionLayout[]): void {
  if (!workspaceId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  const normalized = normalizeLayouts(sections);
  deviceCacheSetItem(
    academicCalendarSectionsCacheKey(workspaceId),
    JSON.stringify(normalized)
  );
}

const GRADE_SECTION_ID_PREFIX = 'grade:';

function collectGradeIdsFromSectionLayouts(sections: PageSectionLayout[] | null | undefined): string[] {
  if (!sections?.length) return [];
  const ids: string[] = [];
  for (const section of sections) {
    const sectionId = section.id?.trim() ?? '';
    if (sectionId.startsWith(GRADE_SECTION_ID_PREFIX)) {
      const gradeId = sectionId.slice(GRADE_SECTION_ID_PREFIX.length).trim();
      if (gradeId) ids.push(gradeId);
    }
    section.gradeIds.forEach((gradeId) => {
      const trimmed = gradeId.trim();
      if (trimmed) ids.push(trimmed);
    });
    const sourceGradeId = section.sourceGradeId?.trim() ?? '';
    if (sourceGradeId) ids.push(sourceGradeId);
  }
  return ids;
}

/** 브라우저 localStorage 섹션·설정 캐시에 남아 있는 클래스 id 참조를 수집한다. */
export function collectReferencedGradeIdsFromLocalCaches(workspaceId: string): string[] {
  if (!workspaceId || typeof window === 'undefined') return [];
  const ids: string[] = [];
  ids.push(...collectGradeIdsFromSectionLayouts(loadGradeTrackSectionsCache(workspaceId)));
  ids.push(...collectGradeIdsFromSectionLayouts(loadAcademicCalendarSectionsCache(workspaceId)));
  try {
    const raw = deviceCacheStorage.getItem(classScheduleCacheKey(workspaceId));
    if (raw) {
      const settings = normalizeClassScheduleSettings(JSON.parse(raw));
      ids.push(...collectGradeIdsFromSectionLayouts(settings.sections));
    }
  } catch {
    // ignore parse failure
  }
  const prefs = loadGradeTrackProgramPreferencesCache(workspaceId);
  if (prefs) {
    ids.push(...Object.keys(prefs.modes), ...Object.keys(prefs.syncReps));
  }
  try {
    const raw = deviceCacheStorage.getItem(programColorCacheKey(workspaceId));
    if (raw) {
      const settings = normalizeProgramColorSettings(JSON.parse(raw));
      ids.push(...Object.keys(settings.colorsByGrade), ...Object.keys(settings.baseTimetableByGrade));
    }
  } catch {
    // ignore parse failure
  }
  return ids.map((gradeId) => gradeId.trim()).filter((gradeId) => gradeId.length > 0);
}

const pendingGradeTrackSections = new Map<string, Promise<PageSectionLayout[]>>();
const pendingAcademicCalendarSections = new Map<string, Promise<PageSectionLayout[]>>();

export type GetSchoolSectionsOptions = { skipLocalCache?: boolean };

export async function getGradeTrackSections(
  workspaceId: string,
  options?: GetSchoolSectionsOptions
): Promise<PageSectionLayout[]> {
  if (!workspaceId) return [...DEFAULT_LAYOUT];
  if (!options?.skipLocalCache) {
    const cached = loadGradeTrackSectionsCache(workspaceId);
    /** 원격 변경은 로그인·새로고침 동기화가 gradeTrackSections 파트를 비교해 이 캐시를 비운다 */
    if (cached != null) {
      recordDevLocalStorageProgramCacheUse('편제섹션·LS');
      return cached;
    }
  }
  const inflightKey = `${workspaceId}:${options?.skipLocalCache ? 'force' : 'def'}`;
  const inflight = pendingGradeTrackSections.get(inflightKey);
  if (inflight) return inflight;
  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('workspaces')
        .select('grade_track_sections')
        .eq('id', workspaceId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const normalized = normalizeLayouts(data?.grade_track_sections);
      saveGradeTrackSectionsCache(workspaceId, normalized);
      return normalized;
    } finally {
      pendingGradeTrackSections.delete(inflightKey);
    }
  })();
  pendingGradeTrackSections.set(inflightKey, task);
  return task;
}

export async function saveGradeTrackSections(
  workspaceId: string,
  sections: PageSectionLayout[]
): Promise<void> {
  const { error } = await supabase
    .from('workspaces')
    .update({ grade_track_sections: sections, updated_at: new Date().toISOString() })
    .eq('id', workspaceId);
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrackSections']);
  /** invalidate가 needsReload=true로 막아 캐시 시드가 실패하면, 직후 load()가 옛 순서/빈 alias 순서로 회귀할 수 있다. */
  allowSchoolScopedCacheSeedAfterInvalidate(workspaceId);
  saveGradeTrackSectionsCache(workspaceId, sections);
}

/** 편제 프로그램: 편제별·시간표별·동기화 모드 및 동기화 대표 시간표명 (학년 ID → 편제명) */
export interface GradeTrackProgramPreferences {
  modes: Record<string, Record<string, TrackProgramMode>>;
  syncReps: Record<string, Record<string, string>>;
  /**
   * 시수 자동 조정: 1·2학기 구분/통합·미포함·학습계획 시수
   * (학년 ID → 편제명 → 설정). workspaces.grade_track_program_preferences JSON에 함께 저장.
   */
  hoursAdjust: Record<string, Record<string, TrackHoursAdjustTrackSettings>>;
  /** 관리자 학습내용 일괄 생성용 출판사 (학년 ID → 편제명 → 출판사) */
  learningPublishers: Record<string, Record<string, string>>;
}

const EMPTY_GRADE_TRACK_PROGRAM_PREFS: GradeTrackProgramPreferences = {
  modes: {},
  syncReps: {},
  hoursAdjust: {},
  learningPublishers: {},
};

function isHoursTermScope(v: unknown): v is TrackHoursTermScope {
  return v === 'split' || v === 'unified';
}

function parseHoursAdjustLearning(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(9999, Math.max(0, Math.round(n)));
}

function normalizeHoursAdjustSettings(raw: unknown): TrackHoursAdjustTrackSettings | null {
  if (isHoursTermScope(raw)) {
    return emptyTrackHoursAdjustSettings({ termScope: raw });
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  return emptyTrackHoursAdjustSettings({
    termScope: isHoursTermScope(obj.termScope) ? obj.termScope : 'split',
    excludeFromAdjust: obj.excludeFromAdjust === true,
    learningTerm1: parseHoursAdjustLearning(obj.learningTerm1),
    learningTerm2: parseHoursAdjustLearning(obj.learningTerm2),
    learningAnnual: parseHoursAdjustLearning(obj.learningAnnual),
  });
}

function parseGradeTrackStringMap(raw: unknown): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  Object.entries(raw as Record<string, unknown>).forEach(([gradeId, trackMap]) => {
    if (!gradeId || typeof trackMap !== 'object' || trackMap === null || Array.isArray(trackMap)) return;
    const entry: Record<string, string> = {};
    Object.entries(trackMap as Record<string, unknown>).forEach(([trackName, val]) => {
      if (!trackName.trim()) return;
      if (typeof val === 'string' && val.trim()) entry[trackName] = val.trim();
    });
    if (Object.keys(entry).length > 0) out[gradeId] = entry;
  });
  return out;
}

export function normalizeGradeTrackProgramPreferences(raw: unknown): GradeTrackProgramPreferences {
  if (!raw || typeof raw !== 'object') {
    return { modes: {}, syncReps: {}, hoursAdjust: {}, learningPublishers: {} };
  }
  const o = raw as Record<string, unknown>;
  const modes: GradeTrackProgramPreferences['modes'] = {};
  const syncReps: GradeTrackProgramPreferences['syncReps'] = {};
  const hoursAdjust: GradeTrackProgramPreferences['hoursAdjust'] = {};
  const learningPublishers = parseGradeTrackStringMap(o.learningPublishers);
  const modesRaw =
    o.modes && typeof o.modes === 'object' && o.modes !== null && !Array.isArray(o.modes)
      ? (o.modes as Record<string, unknown>)
      : {};
  Object.entries(modesRaw).forEach(([gradeId, trackMap]) => {
    if (!gradeId || typeof trackMap !== 'object' || trackMap === null || Array.isArray(trackMap)) return;
    const entry: Record<string, TrackProgramMode> = {};
    Object.entries(trackMap as Record<string, unknown>).forEach(([trackName, val]) => {
      if (!trackName.trim()) return;
      if (val === 'unified' || val === 'split' || val === 'sync') entry[trackName] = val;
    });
    if (Object.keys(entry).length > 0) modes[gradeId] = entry;
  });
  const syncRaw =
    o.syncReps && typeof o.syncReps === 'object' && o.syncReps !== null && !Array.isArray(o.syncReps)
      ? (o.syncReps as Record<string, unknown>)
      : {};
  Object.entries(syncRaw).forEach(([gradeId, repMap]) => {
    if (!gradeId || typeof repMap !== 'object' || repMap === null || Array.isArray(repMap)) return;
    const entry: Record<string, string> = {};
    Object.entries(repMap as Record<string, unknown>).forEach(([trackName, val]) => {
      if (!trackName.trim()) return;
      if (typeof val === 'string' && val.trim()) entry[trackName] = val.trim();
    });
    if (Object.keys(entry).length > 0) syncReps[gradeId] = entry;
  });
  const hoursRaw =
    o.hoursAdjust && typeof o.hoursAdjust === 'object' && o.hoursAdjust !== null && !Array.isArray(o.hoursAdjust)
      ? (o.hoursAdjust as Record<string, unknown>)
      : {};
  Object.entries(hoursRaw).forEach(([gradeId, trackMap]) => {
    if (!gradeId || typeof trackMap !== 'object' || trackMap === null || Array.isArray(trackMap)) return;
    const entry: Record<string, TrackHoursAdjustTrackSettings> = {};
    Object.entries(trackMap as Record<string, unknown>).forEach(([trackName, val]) => {
      if (!trackName.trim()) return;
      const normalized = normalizeHoursAdjustSettings(val);
      if (normalized) entry[trackName] = normalized;
    });
    if (Object.keys(entry).length > 0) hoursAdjust[gradeId] = entry;
  });
  return { modes, syncReps, hoursAdjust, learningPublishers };
}

const GRADE_TRACK_PROGRAM_PREFS_CACHE_VERSION = 'v1';

function gradeTrackProgramPrefsCacheKey(workspaceId: string): string {
  return `cp.gradeTrackProgramPrefs.${GRADE_TRACK_PROGRAM_PREFS_CACHE_VERSION}.${workspaceId}`;
}

export function loadGradeTrackProgramPreferencesCache(workspaceId: string): GradeTrackProgramPreferences | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(gradeTrackProgramPrefsCacheKey(workspaceId));
    if (raw == null || raw === '') return null;
    const parsed = JSON.parse(raw) as unknown;
    return normalizeGradeTrackProgramPreferences(parsed);
  } catch {
    return null;
  }
}

export function saveGradeTrackProgramPreferencesCache(
  workspaceId: string,
  prefs: GradeTrackProgramPreferences
): void {
  if (!workspaceId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  const normalized = normalizeGradeTrackProgramPreferences(prefs);
  deviceCacheSetItem(
    gradeTrackProgramPrefsCacheKey(workspaceId),
    JSON.stringify(normalized)
  );
}

const pendingGradeTrackProgramPrefs = new Map<string, Promise<GradeTrackProgramPreferences>>();

export type GetGradeTrackProgramPreferencesOptions = {
  /** true면 LS 스냅샷을 쓰지 않고 workspaces 행을 조회(편제 편집 화면 등). */
  skipLocalCache?: boolean;
};

export async function getGradeTrackProgramPreferences(
  workspaceId: string,
  options?: GetGradeTrackProgramPreferencesOptions
): Promise<GradeTrackProgramPreferences> {
  if (!workspaceId) return { ...EMPTY_GRADE_TRACK_PROGRAM_PREFS };
  if (!options?.skipLocalCache) {
    const cached = loadGradeTrackProgramPreferencesCache(workspaceId);
    if (cached != null) {
      recordDevLocalStorageProgramCacheUse('편제프로그램설정·LS');
      return cached;
    }
  }

  const inflightKey = `${workspaceId}:${options?.skipLocalCache ? 'force' : 'default'}`;
  const inflight = pendingGradeTrackProgramPrefs.get(inflightKey);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('workspaces')
        .select('grade_track_program_preferences')
        .eq('id', workspaceId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const normalized = normalizeGradeTrackProgramPreferences(data?.grade_track_program_preferences);
      saveGradeTrackProgramPreferencesCache(workspaceId, normalized);
      return normalized;
    } finally {
      pendingGradeTrackProgramPrefs.delete(inflightKey);
    }
  })();

  pendingGradeTrackProgramPrefs.set(inflightKey, task);
  return task;
}

export async function saveGradeTrackProgramPreferences(
  workspaceId: string,
  prefs: GradeTrackProgramPreferences
): Promise<void> {
  if (!workspaceId) return;
  const normalized = normalizeGradeTrackProgramPreferences(prefs);
  const { error } = await supabase
    .from('workspaces')
    .update({
      grade_track_program_preferences: normalized,
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId);
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrackPrefs']);
  allowSchoolScopedCacheSeedAfterInvalidate(workspaceId);
  saveGradeTrackProgramPreferencesCache(workspaceId, normalized);
  reseedGradeTrackProgramPrefsLocalStores(workspaceId, normalized);
}

/** 모달 오픈용: workspaces prefs LS ↔ 레거시 시수조정 LS 병합 (네트워크 없음) */
export function loadHoursAdjustSettingsCacheFirst(
  workspaceId: string,
  gradeId: string
): Record<string, TrackHoursAdjustTrackSettings> {
  const fromLegacy = loadTrackHoursAdjustSettingsMap(workspaceId, gradeId);
  const prefs = loadGradeTrackProgramPreferencesCache(workspaceId);
  const fromPrefsRaw = prefs?.hoursAdjust?.[gradeId];
  if (!fromPrefsRaw || Object.keys(fromPrefsRaw).length === 0) {
    return { ...fromLegacy };
  }
  const fromPrefs: Record<string, TrackHoursAdjustTrackSettings> = {};
  Object.entries(fromPrefsRaw).forEach(([trackName, settings]) => {
    const tn = trackName.trim();
    if (!tn) return;
    const normalized = normalizeHoursAdjustSettings(settings);
    if (normalized) fromPrefs[tn] = normalized;
  });
  /** 레거시로 빈 편제를 채운 뒤 prefs(편제·기준시수 페이지 저장본)를 덮어씀 */
  return { ...fromLegacy, ...fromPrefs };
}

/**
 * 시수 조정 편제 설정을 grade_track_program_preferences에 병합 저장.
 * (편제·기준시수 페이지와 동일 DB 컬럼·LS 캐시)
 */
export async function persistHoursAdjustSettingsForGrade(
  workspaceId: string,
  gradeId: string,
  settings: Record<string, TrackHoursAdjustTrackSettings>
): Promise<void> {
  if (!workspaceId || !gradeId) return;
  let prefs = loadGradeTrackProgramPreferencesCache(workspaceId);
  if (!prefs) {
    prefs = await getGradeTrackProgramPreferences(workspaceId);
  }
  const next: GradeTrackProgramPreferences = {
    modes: prefs.modes,
    syncReps: prefs.syncReps,
    hoursAdjust: {
      ...(prefs.hoursAdjust ?? {}),
      [gradeId]: settings,
    },
    learningPublishers: prefs.learningPublishers ?? {},
  };
  await saveGradeTrackProgramPreferences(workspaceId, next);
  saveTrackHoursAdjustSettingsMap(workspaceId, gradeId, settings);
}

export async function getAcademicCalendarSections(
  workspaceId: string,
  options?: GetSchoolSectionsOptions
): Promise<PageSectionLayout[]> {
  if (!workspaceId) return [...DEFAULT_LAYOUT];
  if (!options?.skipLocalCache) {
    const cached = loadAcademicCalendarSectionsCache(workspaceId);
    if (cached != null) {
      recordDevLocalStorageProgramCacheUse('학사달력섹션·LS');
      return cached;
    }
  }
  const inflightKey = `${workspaceId}:${options?.skipLocalCache ? 'force' : 'def'}`;
  const inflight = pendingAcademicCalendarSections.get(inflightKey);
  if (inflight) return inflight;
  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('workspaces')
        .select('academic_calendar_sections')
        .eq('id', workspaceId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const normalized = normalizeLayouts(data?.academic_calendar_sections);
      saveAcademicCalendarSectionsCache(workspaceId, normalized);
      return normalized;
    } finally {
      pendingAcademicCalendarSections.delete(inflightKey);
    }
  })();
  pendingAcademicCalendarSections.set(inflightKey, task);
  return task;
}

export async function saveAcademicCalendarSections(
  workspaceId: string,
  sections: PageSectionLayout[]
): Promise<void> {
  const { error } = await supabase
    .from('workspaces')
    .update({ academic_calendar_sections: sections, updated_at: new Date().toISOString() })
    .eq('id', workspaceId);
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(workspaceId, ['academicCalendar']);
  saveAcademicCalendarSectionsCache(workspaceId, sections);
}

/** 클래스(grade)별 고정·전담·라벨링·프로젝트 색상. 저장 버튼 시에만 반영. */
export interface GradeProgramColors {
  regular: string[];
  dedicated: string[];
  baseExclude: string[];
  /**
   * 학기 공통·레거시 통합 목록(연간 툴바 등).
   * normalize 시 projectByTerm 합집합으로 맞춤.
   */
  project: string[];
  /** 기초시간표 학기별 프로젝트 색상 */
  projectByTerm: { term1: string[]; term2: string[] };
  labels?: GradeProgramColorLabels;
}

export interface GradeProgramColorLabels {
  regular: Record<string, string>;
  dedicated: Record<string, string>;
  baseExclude: Record<string, string>;
  project: Record<string, string>;
  projectTerm1: Record<string, string>;
  projectTerm2: Record<string, string>;
}

export interface BaseTimetableCellValue {
  name: string;
  colorHex: string;
}

export interface BaseTimetableGrid {
  mon: BaseTimetableCellValue[];
  tue: BaseTimetableCellValue[];
  wed: BaseTimetableCellValue[];
  thu: BaseTimetableCellValue[];
  fri: BaseTimetableCellValue[];
}

/** 클래스별 1·2학기 기초시간표 */
export interface BaseTimetableByTerm {
  term1: BaseTimetableGrid;
  term2: BaseTimetableGrid;
}

export interface ProgramColorSettings {
  colorsByGrade: Record<string, GradeProgramColors>;
  baseTimetableByGrade: Record<string, BaseTimetableByTerm>;
  /**
   * 레거시 공통 팔레트 또는 colorsByGrade에 없는 클래스의 기본값.
   * 시간표 설정에서 클래스별로 모두 저장하면 null로 둘 수 있음.
   */
  workspaceFallback: GradeProgramColors | null;
}

const DEFAULT_GRADE_PROGRAM_COLORS: GradeProgramColors = createDefaultGradeProgramColors();

function createEmptyGradeProgramColorLabels(): GradeProgramColorLabels {
  return {
    regular: {},
    dedicated: {},
    baseExclude: {},
    project: {},
    projectTerm1: {},
    projectTerm2: {},
  };
}

function normalizeColorLabelMap(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([hex, label]) => {
    const nHex = hex.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(nHex)) return;
    if (typeof label !== 'string') return;
    const trimmed = label.trim();
    if (!trimmed) return;
    out[nHex] = trimmed;
  });
  return out;
}

function normalizeGradeProgramColorLabels(raw: unknown): GradeProgramColorLabels {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return createEmptyGradeProgramColorLabels();
  const o = raw as Record<string, unknown>;
  const sharedProject = normalizeColorLabelMap(o.project);
  return {
    regular: normalizeColorLabelMap(o.regular),
    dedicated: normalizeColorLabelMap(o.dedicated),
    baseExclude: normalizeColorLabelMap(o.baseExclude),
    project: sharedProject,
    projectTerm1: {
      ...sharedProject,
      ...normalizeColorLabelMap(o.projectTerm1),
    },
    projectTerm2: {
      ...sharedProject,
      ...normalizeColorLabelMap(o.projectTerm2),
    },
  };
}

function mergeUniqueHexLists(...lists: string[][]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  lists.flat().forEach((hex) => {
    const n = hex.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(n) || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });
  return out;
}

function normalizeProjectByTerm(
  rawByTerm: unknown,
  legacyProject: string[]
): { term1: string[]; term2: string[] } {
  if (rawByTerm && typeof rawByTerm === 'object' && !Array.isArray(rawByTerm)) {
    const o = rawByTerm as Record<string, unknown>;
    const term1 = normalizeHexColorList(o.term1);
    const term2 = normalizeHexColorList(o.term2);
    if (term1.length > 0 || term2.length > 0) {
      return {
        term1: term1.length > 0 ? term1 : [...legacyProject],
        term2: term2.length > 0 ? term2 : [...legacyProject],
      };
    }
  }
  return { term1: [...legacyProject], term2: [...legacyProject] };
}

/** 학기별 프로젝트 색상 목록 */
export function getProjectColorsForTerm(colors: GradeProgramColors, term: 1 | 2): string[] {
  const by = colors.projectByTerm;
  if (by) return term === 1 ? by.term1 ?? [] : by.term2 ?? [];
  return colors.project ?? [];
}

export function getProjectColorLabelForTerm(
  colors: GradeProgramColors,
  term: 1 | 2,
  hex: string
): string {
  const key = hex.trim().toLowerCase();
  if (!key) return '';
  const labels = colors.labels;
  if (!labels) return '';
  if (term === 1) return labels.projectTerm1?.[key] ?? labels.project?.[key] ?? '';
  return labels.projectTerm2?.[key] ?? labels.project?.[key] ?? '';
}

/** 전담·제외·프로젝트가 비어 있고 고정만 레거시 1색이면 새 섹션 기본 팔레트로 보정 */
function coalesceLegacyUnsetProgramColors(colors: GradeProgramColors): GradeProgramColors {
  const projectCount =
    (colors.project?.length ?? 0) +
    (colors.projectByTerm?.term1?.length ?? 0) +
    (colors.projectByTerm?.term2?.length ?? 0);
  if (colors.dedicated.length > 0 || colors.baseExclude.length > 0 || projectCount > 0) {
    return colors;
  }
  const reg = colors.regular.map((h) => h.trim().toLowerCase());
  const legacyUnset =
    reg.length === 0 || (reg.length === 1 && reg[0] === LEGACY_DEFAULT_REGULAR_COLOR.toLowerCase());
  if (!legacyUnset) return colors;
  return {
    ...createDefaultGradeProgramColors(),
    labels: normalizeGradeProgramColorLabels(colors.labels),
  };
}

const DEFAULT_PROGRAM_COLOR_SETTINGS: ProgramColorSettings = {
  colorsByGrade: {},
  baseTimetableByGrade: {},
  workspaceFallback: { ...DEFAULT_GRADE_PROGRAM_COLORS },
};

function normalizeHexColorList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v));
}

export function normalizeGradeProgramColors(raw: unknown): GradeProgramColors {
  if (!raw || typeof raw !== 'object') {
    return { ...createDefaultGradeProgramColors(), labels: createEmptyGradeProgramColorLabels() };
  }
  const o = raw as Record<string, unknown>;
  const regular = normalizeHexColorList(o.regular);
  const dedicated = normalizeHexColorList(o.dedicated);
  const baseExclude = normalizeHexColorList(o.baseExclude);
  const legacyProject = normalizeHexColorList(o.project);
  const projectByTerm = normalizeProjectByTerm(o.projectByTerm, legacyProject);
  const project = mergeUniqueHexLists(legacyProject, projectByTerm.term1, projectByTerm.term2);
  const labels = normalizeGradeProgramColorLabels(o.labels);
  return coalesceLegacyUnsetProgramColors({
    regular: regular.length > 0 ? regular : DEFAULT_GRADE_PROGRAM_COLORS.regular,
    dedicated,
    baseExclude,
    project,
    projectByTerm,
    labels,
  });
}

/**
 * 저장된 설정에서 특정 클래스(gradeId)에 적용할 팔레트를 반환합니다.
 * colorsByGrade에 해당 키가 있으면 우선, 없으면 workspaceFallback, 둘 다 없으면 기본값.
 */
export function resolveGradeProgramColors(
  settings: ProgramColorSettings,
  gradeId: string
): GradeProgramColors {
  const gid = (gradeId ?? '').trim();
  if (gid && Object.prototype.hasOwnProperty.call(settings.colorsByGrade, gid)) {
    return normalizeGradeProgramColors(settings.colorsByGrade[gid]);
  }
  if (settings.workspaceFallback) {
    return normalizeGradeProgramColors(settings.workspaceFallback);
  }
  return { ...createDefaultGradeProgramColors(), labels: createEmptyGradeProgramColorLabels() };
}

const EMPTY_BASE_TIMETABLE_GRID: BaseTimetableGrid = {
  mon: Array.from({ length: 10 }, () => ({ name: '', colorHex: '' })),
  tue: Array.from({ length: 10 }, () => ({ name: '', colorHex: '' })),
  wed: Array.from({ length: 10 }, () => ({ name: '', colorHex: '' })),
  thu: Array.from({ length: 10 }, () => ({ name: '', colorHex: '' })),
  fri: Array.from({ length: 10 }, () => ({ name: '', colorHex: '' })),
};

function normalizeDayCells(raw: unknown): BaseTimetableCellValue[] {
  if (!Array.isArray(raw)) return Array.from({ length: 10 }, () => ({ name: '', colorHex: '' }));
  const normalized: BaseTimetableCellValue[] = raw.slice(0, 10).map((v) => {
    if (typeof v === 'string') return { name: v, colorHex: '' };
    if (!v || typeof v !== 'object') return { name: '', colorHex: '' };
    const obj = v as Record<string, unknown>;
    const name = typeof obj.name === 'string' ? obj.name : '';
    const colorHex =
      typeof obj.colorHex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(obj.colorHex)
        ? obj.colorHex
        : '';
    return { name, colorHex };
  });
  while (normalized.length < 10) normalized.push({ name: '', colorHex: '' });
  return normalized;
}

export function normalizeBaseTimetableGrid(raw: unknown): BaseTimetableGrid {
  if (!raw || typeof raw !== 'object') {
    return {
      mon: [...EMPTY_BASE_TIMETABLE_GRID.mon],
      tue: [...EMPTY_BASE_TIMETABLE_GRID.tue],
      wed: [...EMPTY_BASE_TIMETABLE_GRID.wed],
      thu: [...EMPTY_BASE_TIMETABLE_GRID.thu],
      fri: [...EMPTY_BASE_TIMETABLE_GRID.fri],
    };
  }
  const obj = raw as Record<string, unknown>;
  return {
    mon: normalizeDayCells(obj.mon),
    tue: normalizeDayCells(obj.tue),
    wed: normalizeDayCells(obj.wed),
    thu: normalizeDayCells(obj.thu),
    fri: normalizeDayCells(obj.fri),
  };
}

export function cloneBaseTimetableGrid(grid: BaseTimetableGrid): BaseTimetableGrid {
  return {
    mon: grid.mon.map((c) => ({ ...c })),
    tue: grid.tue.map((c) => ({ ...c })),
    wed: grid.wed.map((c) => ({ ...c })),
    thu: grid.thu.map((c) => ({ ...c })),
    fri: grid.fri.map((c) => ({ ...c })),
  };
}

export function createEmptyBaseTimetableByTerm(): BaseTimetableByTerm {
  return {
    term1: normalizeBaseTimetableGrid(null),
    term2: normalizeBaseTimetableGrid(null),
  };
}

/**
 * 레거시(단일 그리드)는 1·2학기에 동일 내용으로 복제.
 * 신형식 `{ term1, term2 }` 는 각 학기 그리드로 정규화(term2 없으면 term1 복제).
 */
export function normalizeBaseTimetableByTerm(raw: unknown): BaseTimetableByTerm {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return createEmptyBaseTimetableByTerm();
  }
  const o = raw as Record<string, unknown>;
  const looksLikeTermed =
    Object.prototype.hasOwnProperty.call(o, 'term1') ||
    Object.prototype.hasOwnProperty.call(o, 'term2');
  if (looksLikeTermed) {
    const term1 = normalizeBaseTimetableGrid(o.term1);
    const term2 =
      o.term2 != null ? normalizeBaseTimetableGrid(o.term2) : cloneBaseTimetableGrid(term1);
    return { term1, term2 };
  }
  const legacy = normalizeBaseTimetableGrid(raw);
  return { term1: legacy, term2: cloneBaseTimetableGrid(legacy) };
}

export function getBaseTimetableGridForTerm(
  byTerm: BaseTimetableByTerm | null | undefined,
  term: 1 | 2
): BaseTimetableGrid {
  if (!byTerm) return normalizeBaseTimetableGrid(null);
  return term === 1 ? byTerm.term1 : byTerm.term2;
}

function normalizeProgramColorSettings(raw: unknown): ProgramColorSettings {
  if (!raw || typeof raw !== 'object') {
    return {
      colorsByGrade: {},
      baseTimetableByGrade: {},
      workspaceFallback: { ...DEFAULT_GRADE_PROGRAM_COLORS },
    };
  }
  const o = raw as Record<string, unknown>;

  const baseTimetableByGradeRaw =
    o.baseTimetableByGrade && typeof o.baseTimetableByGrade === 'object'
      ? (o.baseTimetableByGrade as Record<string, unknown>)
      : {};
  const baseTimetableByGrade: Record<string, BaseTimetableByTerm> = {};
  Object.keys(baseTimetableByGradeRaw).forEach((gradeId) => {
    baseTimetableByGrade[gradeId] = normalizeBaseTimetableByTerm(baseTimetableByGradeRaw[gradeId]);
  });

  const colorsByGrade: Record<string, GradeProgramColors> = {};
  if (o.colorsByGrade && typeof o.colorsByGrade === 'object') {
    const rawMap = o.colorsByGrade as Record<string, unknown>;
    Object.keys(rawMap).forEach((gradeId) => {
      if (!gradeId.trim()) return;
      colorsByGrade[gradeId] = normalizeGradeProgramColors(rawMap[gradeId]);
    });
  }

  let workspaceFallback: GradeProgramColors | null = null;
  if (o.workspaceFallback && typeof o.workspaceFallback === 'object') {
    workspaceFallback = normalizeGradeProgramColors(o.workspaceFallback);
  }

  if (Object.keys(colorsByGrade).length === 0) {
    const legacyRegular = normalizeHexColorList(o.regular);
    const legacyDedicated = normalizeHexColorList(o.dedicated);
    const legacyExclude = normalizeHexColorList(o.baseExclude);
    if (
      legacyRegular.length > 0 ||
      legacyDedicated.length > 0 ||
      legacyExclude.length > 0 ||
      o.regular !== undefined ||
      o.dedicated !== undefined ||
      o.baseExclude !== undefined
    ) {
      workspaceFallback = normalizeGradeProgramColors({
        regular:
          legacyRegular.length > 0 ? legacyRegular : DEFAULT_GRADE_PROGRAM_COLORS.regular,
        dedicated: legacyDedicated,
        baseExclude: legacyExclude,
        project: normalizeHexColorList(o.project),
        labels: createEmptyGradeProgramColorLabels(),
      });
    } else if (!workspaceFallback) {
      workspaceFallback = { ...DEFAULT_GRADE_PROGRAM_COLORS };
    }
  } else if (!workspaceFallback) {
    workspaceFallback = null;
  }

  return {
    colorsByGrade,
    baseTimetableByGrade,
    workspaceFallback,
  };
}

/** 네트워크 없이 시간표 색 캐시만 읽는다. */
export function loadProgramColorSettingsCache(workspaceId: string): ProgramColorSettings | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(programColorCacheKey(workspaceId));
    if (!raw) return null;
    return normalizeProgramColorSettings(JSON.parse(raw));
  } catch {
    return null;
  }
}

export async function getProgramColorSettings(workspaceId: string): Promise<ProgramColorSettings> {
  const cached = loadProgramColorSettingsCache(workspaceId);
  if (cached) {
    recordDevLocalStorageProgramCacheUse('시간표색상·LS');
    return cached;
  }
  const { data, error } = await supabase
    .from('workspaces')
    .select('program_color_settings')
    .eq('id', workspaceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const normalized = normalizeProgramColorSettings(data?.program_color_settings);
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(programColorCacheKey(workspaceId), JSON.stringify(normalized));
    } catch {
      // ignore quota
    }
  }
  return normalized;
}

export async function saveProgramColorSettings(
  workspaceId: string,
  settings: ProgramColorSettings
): Promise<void> {
  const { error } = await supabase
    .from('workspaces')
    .update({
      program_color_settings: {
        colorsByGrade: settings.colorsByGrade,
        baseTimetableByGrade: settings.baseTimetableByGrade,
        workspaceFallback: settings.workspaceFallback,
      },
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId);
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(workspaceId, ['programColors']);
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(programColorCacheKey(workspaceId), JSON.stringify(settings));
    } catch {
      // ignore quota
    }
  }
}

export interface ClassScheduleRow {
  kind: 'class' | 'activity' | 'after_school';
  name: string;
  startTime: string;
  durationMinutes: string;
  endTime: string;
}

/** 요일별 설정 시 슬롯별 시각만 별도 저장(구분·이름은 sectionRows 공통) */
export interface ClassScheduleRowTimes {
  startTime: string;
  durationMinutes: string;
  endTime: string;
}

export type SectionTimeMode = 'common' | 'weekday';

export type WeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';

export interface WeekdayMaxPeriod {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
}

export interface ClassScheduleSettings {
  sections: PageSectionLayout[];
  sectionRows: Record<string, ClassScheduleRow[]>;
  sectionWeekdayMax: Record<string, WeekdayMaxPeriod>;
  /** 섹션별 시각 입력 모드. 없으면 common */
  sectionTimeMode?: Record<string, SectionTimeMode>;
  /** weekday 모드일 때 요일별 시각(최대 10슬롯). sparse 허용 */
  sectionWeekdayRowTimes?: Record<string, Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>>>;
}

const DEFAULT_CLASS_SCHEDULE_ROW: ClassScheduleRow = {
  kind: 'class',
  name: '',
  startTime: '',
  durationMinutes: '',
  endTime: '',
};

const DEFAULT_WEEKDAY_MAX_PERIOD: WeekdayMaxPeriod = {
  mon: 0,
  tue: 0,
  wed: 0,
  thu: 0,
  fri: 0,
};

function createDefaultRows(): ClassScheduleRow[] {
  return Array.from({ length: 10 }, () => ({ ...DEFAULT_CLASS_SCHEDULE_ROW }));
}

const DEFAULT_CLASS_SCHEDULE_SETTINGS: ClassScheduleSettings = {
  sections: DEFAULT_LAYOUT,
  sectionRows: { base: createDefaultRows() },
  sectionWeekdayMax: { base: { ...DEFAULT_WEEKDAY_MAX_PERIOD } },
};

function normalizeClassScheduleRows(raw: unknown): ClassScheduleRow[] {
  if (!Array.isArray(raw)) return createDefaultRows();
  let classCount = 0;
  let afterSchoolCount = 0;
  const normalized: ClassScheduleRow[] = raw
    .slice(0, 10)
    .map((item) => {
      if (!item || typeof item !== 'object') return { ...DEFAULT_CLASS_SCHEDULE_ROW };
      const row = item as Record<string, unknown>;
      const rawKind = row.kind;
      const kind: ClassScheduleRow['kind'] =
        rawKind === 'class' || rawKind === 'activity' || rawKind === 'after_school'
          ? rawKind
          : rawKind === 'other'
            ? 'after_school'
            : 'class';
      let name = typeof row.name === 'string' ? row.name : '';
      if (kind === 'class') {
        classCount += 1;
        if (!name.trim()) {
          name = `${classCount}교시`;
        }
      } else if (kind === 'after_school') {
        afterSchoolCount += 1;
        if (!name.trim()) {
          name = `방과후${afterSchoolCount}`;
        }
      }
      /* activity: name은 저장값 그대로(클래스 스케줄 '이름'·활동명) */
      const startTime = typeof row.startTime === 'string' ? row.startTime : '';
      const durationMinutes = typeof row.durationMinutes === 'string' ? row.durationMinutes : '';
      const endTime = typeof row.endTime === 'string' ? row.endTime : '';
      return { kind, name, startTime, durationMinutes, endTime };
    });
  while (normalized.length < 10) normalized.push({ ...DEFAULT_CLASS_SCHEDULE_ROW });
  return normalized;
}

function toBoundedPeriod(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  const floored = Math.floor(value);
  if (floored < 0) return 0;
  if (floored > 10) return 10;
  return floored;
}

function normalizeWeekdayMaxPeriod(raw: unknown): WeekdayMaxPeriod {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_WEEKDAY_MAX_PERIOD };
  const obj = raw as Record<string, unknown>;
  return {
    mon: toBoundedPeriod(obj.mon),
    tue: toBoundedPeriod(obj.tue),
    wed: toBoundedPeriod(obj.wed),
    thu: toBoundedPeriod(obj.thu),
    fri: toBoundedPeriod(obj.fri),
  };
}

function normalizeClassScheduleRowTimes(raw: unknown): ClassScheduleRowTimes {
  if (!raw || typeof raw !== 'object') {
    return { startTime: '', durationMinutes: '', endTime: '' };
  }
  const row = raw as Record<string, unknown>;
  return {
    startTime: typeof row.startTime === 'string' ? row.startTime : '',
    durationMinutes: typeof row.durationMinutes === 'string' ? row.durationMinutes : '',
    endTime: typeof row.endTime === 'string' ? row.endTime : '',
  };
}

function normalizeWeekdayRowTimesList(raw: unknown): ClassScheduleRowTimes[] {
  if (!Array.isArray(raw)) return [];
  const normalized = raw.slice(0, 10).map(normalizeClassScheduleRowTimes);
  while (normalized.length < 10) {
    normalized.push({ startTime: '', durationMinutes: '', endTime: '' });
  }
  return normalized;
}

function normalizeSectionTimeMode(raw: unknown): SectionTimeMode {
  return raw === 'weekday' ? 'weekday' : 'common';
}

function normalizeSectionWeekdayRowTimes(raw: unknown): Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>> {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as Record<string, unknown>;
  const out: Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>> = {};
  (['mon', 'tue', 'wed', 'thu', 'fri'] as const).forEach((key) => {
    if (obj[key] != null) out[key] = normalizeWeekdayRowTimesList(obj[key]);
  });
  return out;
}

function normalizeClassScheduleSettings(raw: unknown): ClassScheduleSettings {
  if (!raw || typeof raw !== 'object') return DEFAULT_CLASS_SCHEDULE_SETTINGS;
  const obj = raw as Record<string, unknown>;
  const sections = normalizeLayouts(obj.sections);
  const rowsRaw =
    obj.sectionRows && typeof obj.sectionRows === 'object'
      ? (obj.sectionRows as Record<string, unknown>)
      : {};
  const weekdayRaw =
    obj.sectionWeekdayMax && typeof obj.sectionWeekdayMax === 'object'
      ? (obj.sectionWeekdayMax as Record<string, unknown>)
      : {};
  const timeModeRaw =
    obj.sectionTimeMode && typeof obj.sectionTimeMode === 'object'
      ? (obj.sectionTimeMode as Record<string, unknown>)
      : {};
  const weekdayTimesRaw =
    obj.sectionWeekdayRowTimes && typeof obj.sectionWeekdayRowTimes === 'object'
      ? (obj.sectionWeekdayRowTimes as Record<string, unknown>)
      : {};
  const sectionRows: Record<string, ClassScheduleRow[]> = {};
  const sectionWeekdayMax: Record<string, WeekdayMaxPeriod> = {};
  const sectionTimeMode: Record<string, SectionTimeMode> = {};
  const sectionWeekdayRowTimes: Record<string, Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>>> = {};
  sections.forEach((s) => {
    sectionRows[s.id] = normalizeClassScheduleRows(rowsRaw[s.id]);
    sectionWeekdayMax[s.id] = normalizeWeekdayMaxPeriod(weekdayRaw[s.id]);
    sectionTimeMode[s.id] = normalizeSectionTimeMode(timeModeRaw[s.id]);
    const dayTimes = normalizeSectionWeekdayRowTimes(weekdayTimesRaw[s.id]);
    if (Object.keys(dayTimes).length) sectionWeekdayRowTimes[s.id] = dayTimes;
  });
  return {
    sections,
    sectionRows,
    sectionWeekdayMax,
    sectionTimeMode,
    sectionWeekdayRowTimes: Object.keys(sectionWeekdayRowTimes).length ? sectionWeekdayRowTimes : undefined,
  };
}

export async function getClassScheduleSettings(
  workspaceId: string,
  options?: { skipLocalCache?: boolean }
): Promise<ClassScheduleSettings> {
  if (workspaceId && typeof window !== 'undefined' && !options?.skipLocalCache) {
    try {
      const cached = deviceCacheStorage.getItem(classScheduleCacheKey(workspaceId));
      if (cached) {
        recordDevLocalStorageProgramCacheUse('기초시간표설정·LS');
        return normalizeClassScheduleSettings(JSON.parse(cached));
      }
    } catch {
      // ignore parse failure
    }
  }
  const { data, error } = await supabase
    .from('workspaces')
    .select('class_schedule_settings')
    .eq('id', workspaceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const normalized = normalizeClassScheduleSettings(data?.class_schedule_settings);
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(classScheduleCacheKey(workspaceId), JSON.stringify(normalized));
    } catch {
      // ignore quota
    }
  }
  return normalized;
}

export async function saveClassScheduleSettings(
  workspaceId: string,
  settings: ClassScheduleSettings
): Promise<void> {
  const normalizedRows: Record<string, ClassScheduleRow[]> = {};
  const normalizedWeekdayMax: Record<string, WeekdayMaxPeriod> = {};
  const normalizedTimeMode: Record<string, SectionTimeMode> = {};
  const normalizedWeekdayRowTimes: Record<string, Partial<Record<WeekdayKey, ClassScheduleRowTimes[]>>> = {};
  settings.sections.forEach((s) => {
    normalizedRows[s.id] = normalizeClassScheduleRows(settings.sectionRows[s.id]);
    normalizedWeekdayMax[s.id] = normalizeWeekdayMaxPeriod(settings.sectionWeekdayMax[s.id]);
    normalizedTimeMode[s.id] = normalizeSectionTimeMode(settings.sectionTimeMode?.[s.id]);
    const dayTimes = normalizeSectionWeekdayRowTimes(settings.sectionWeekdayRowTimes?.[s.id]);
    if (Object.keys(dayTimes).length) normalizedWeekdayRowTimes[s.id] = dayTimes;
  });
  const dbPayload: ClassScheduleSettings = {
    sections: settings.sections,
    sectionRows: normalizedRows,
    sectionWeekdayMax: normalizedWeekdayMax,
    sectionTimeMode: normalizedTimeMode,
    ...(Object.keys(normalizedWeekdayRowTimes).length
      ? { sectionWeekdayRowTimes: normalizedWeekdayRowTimes }
      : {}),
  };
  const { error } = await supabase
    .from('workspaces')
    .update({
      class_schedule_settings: dbPayload,
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId);
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(workspaceId, ['classSchedule']);
  const normalized: ClassScheduleSettings = dbPayload;
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    try {
      deviceCacheStorage.setItem(classScheduleCacheKey(workspaceId), JSON.stringify(normalized));
    } catch {
      /* ignore quota */
    }
  }
}

